import React from "react";
import { Box, Link, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

const markdownSx = {
  fontSize: "0.875rem",
  lineHeight: 1.6,
  wordBreak: "break-word" as const,
  "& > *:first-of-type": { mt: 0 },
  "& > *:last-child": { mb: 0 },
  "& p": { m: 0, mb: 1 },
  "& h1": { fontSize: "1.25rem", fontWeight: 700, mt: 1.5, mb: 0.75 },
  "& h2": { fontSize: "1.125rem", fontWeight: 700, mt: 1.25, mb: 0.75 },
  "& h3": { fontSize: "1rem", fontWeight: 600, mt: 1, mb: 0.5 },
  "& h4, & h5, & h6": { fontSize: "0.9375rem", fontWeight: 600, mt: 1, mb: 0.5 },
  "& ul, & ol": { pl: 2.5, mb: 1, mt: 0 },
  "& li": { mb: 0.25 },
  "& li > p": { mb: 0.25 },
  "& blockquote": {
    borderLeft: 3,
    borderColor: "primary.main",
    pl: 1.5,
    ml: 0,
    my: 1,
    color: "text.secondary",
  },
  "& hr": { border: 0, borderTop: 1, borderColor: "divider", my: 1.5 },
  "& a": { color: "primary.main", textDecoration: "underline" },
  "& table": {
    width: "100%",
    borderCollapse: "collapse",
    mb: 1,
    fontSize: "0.8125rem",
    display: "block",
    overflowX: "auto",
  },
  "& th, & td": {
    border: 1,
    borderColor: "divider",
    px: 1,
    py: 0.75,
    textAlign: "left",
    verticalAlign: "top",
  },
  "& th": { bgcolor: "action.hover", fontWeight: 600 },
  "& pre": {
    m: 0,
    mb: 1,
    p: 1.25,
    borderRadius: 1,
    overflow: "auto",
    fontSize: "0.8125rem",
    bgcolor: (theme: Theme) =>
      theme.palette.mode === "dark" ? theme.palette.common.black : theme.palette.grey[900],
    color: (theme: Theme) =>
      theme.palette.mode === "dark" ? theme.palette.grey[300] : theme.palette.grey[100],
    border: 1,
    borderColor: "divider",
  },
  "& pre code": {
    fontFamily: "ui-monospace, monospace",
    bgcolor: "transparent",
    p: 0,
  },
  "& :not(pre) > code": {
    fontFamily: "ui-monospace, monospace",
    fontSize: "0.8125rem",
    px: 0.5,
    py: 0.125,
    borderRadius: 0.5,
    bgcolor: "action.selected",
  },
};

const components: Components = {
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" variant="body2">
      {children}
    </Link>
  ),
};

interface AgentMarkdownMessageProps {
  content: string;
  /** When true, text inherits parent color (e.g. user bubble on primary). */
  invertColors?: boolean;
}

const AgentMarkdownMessage: React.FC<AgentMarkdownMessageProps> = ({
  content,
  invertColors = false,
}) => {
  if (!content.trim()) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        (No message content)
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        ...markdownSx,
        ...(invertColors
          ? {
              color: "inherit",
              "& a": { color: "inherit", fontWeight: 600 },
              "& th": { bgcolor: "rgba(255,255,255,0.12)" },
              "& :not(pre) > code": { bgcolor: "rgba(255,255,255,0.15)" },
              "& pre": { bgcolor: "rgba(0,0,0,0.25)", color: "inherit" },
            }
          : {}),
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default AgentMarkdownMessage;
